import renderInputField from 'components/FormControls/textInput'
import { Button, Icon, Tooltip } from 'gu-ui-kit'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import SERVICES_API_V1 from 'api/services/v1'
import formStyles from 'modules/Forms/common-form.module.scss'
import clsx from 'clsx'

export const VkAdsSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      vk_ads_account_id: Yup.number()
        .test(
          'isVkAdsAccountId',
          'services::forms.register_account.vk-ads.fields.vk_ads_account_id.validation_messages.incorrect_format',
          function test(value) {
            const { path, createError } = this

            const accountRegex = new RegExp(/^(\d{8})$/)
            const correctFormat = accountRegex.test(value)

            return (
              correctFormat ||
              createError({
                path,
                message:
                  'services::forms.register_account.vk-ads.fields.vk_ads_account_id.validation_messages.incorrect_format',
              })
            )
          },
        )
        .required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended())

const ButtonVkAdsId = ({ openConnectWindow, ...props }) => {
  const { t } = useTranslation()

  return (
    <Button
      noWrap
      variant={'ghost'}
      link
      type="button"
      onClick={openConnectWindow}
      {...props}
    >
      {t('services::forms.register_account.vk-ads.fields.vk_ads_account_id.get_url_label')}
    </Button>
  )
}

ButtonVkAdsId.propTypes = {
  openConnectWindow: PropTypes.func,
}

let CreateVkAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  array,
  networkType,
  form,
  networkId,
  multiLogins,
  submitFailed,
}) => {
  let childWindow
  const { t } = useTranslation([])
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const { values = {} } = useSelector((state) => state.form[form])
  const { vk_ads_account_id, advertiser_info } = values
  const breakpoint = useBreakpoint()

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  global.getBack = useCallback(
    ({ id = null }) => {
      !!childWindow && childWindow.close()
      childWindow = null
      !!id && autofill('vk_ads_account_id', id)
    },
    [childWindow, autofill],
  )

  const openConnectWindow = useCallback(() => {
    childWindow = global.open(
      '',
      'Vk Ads oAuth dialog',
      'resizable,toolbar=yes,location=yes,scrollbars=yes,menubar=yes,width=800,height=600,top=0,left=0',
    )
    SERVICES_API_V1.getNetworkAuthUrl({ networkType })
      .then(({ response: { data = {} } = {} }) => {
        if (!childWindow) {
          return
        }
        const { url = null } = data
        if (url) {
          childWindow.location = url
          childWindow.focus()
        } else {
          childWindow.close()
        }
      })
      .catch(() => {
        childWindow && childWindow.close()
      })
  }, [childWindow, networkType])

  return (
    <FormFrame
      onSubmit={handleSubmit(submit)}
      multiLogins={multiLogins}
      form={form}
      advertiser_info={advertiser_info}
      submitFailed={submitFailed}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx(formStyles['row_with-tooltip'], 'margin-bottom-24')}>
          <Field
            type="text"
            fullWidth
            name="vk_ads_account_id"
            required
            normalize={(value) => {
              const replaced = value.replace(/\D/g, '').slice(0, 8)
              return replaced.length > 0 ? parseInt(replaced, 10) : undefined
            }}
            component={renderInputField}
            label={t('services::forms.register_account.vk-ads.fields.vk_ads_account_id.label')}
            t={t}
            InputProps={{
              endAdornment: !vk_ads_account_id && breakpoint.md && (
                <ButtonVkAdsId
                  size={'m'}
                  openConnectWindow={openConnectWindow}
                />
              ),
            }}
            helperText={
              !vk_ads_account_id &&
              !breakpoint.md && (
                <ButtonVkAdsId
                  size={'s'}
                  openConnectWindow={openConnectWindow}
                />
              )
            }
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.vk-ads.fields.vk_ads_account_id.tooltip')}
              placement="top"
              arrow
              tooltipClassName={formStyles['tooltip']}
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-48', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            autofill={autofill}
            multiLogins={multiLogins}
            array={array}
            networkId={networkId}
            label={'services::forms.register_account.vk-ads.fields.login.label'}
          />
        </div>

    </FormFrame>
  )
}

CreateVkAdsAccountForm = reduxForm({
  form: 'CreateVkAdsAccountForm',
  initialValues: {
    network: '',
    login: '',
    vk_ads_account_id: undefined,
    payment_profile_id: undefined,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateVkAdsAccountForm)

CreateVkAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  valid: PropTypes.bool,
  pristine: PropTypes.bool,
  dirty: PropTypes.bool,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkType: PropTypes.string,
  form: PropTypes.string,
  initialValues: PropTypes.object,
  networkId: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  submitFailed: PropTypes.bool,
}

export default CreateVkAdsAccountForm
